// React
import { useState } from 'react'

// MUI Components
import { Box } from '@mui/material'

// MUI Icons
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'

// Custom Components
import CustomButton from '../v2/general/CustomButton/CustomButton'
import TableMotoristas from './TableMotoristas'
import TableMotoristaFilters from './TableMotoristasFilters'
import SubmittedWithoutChanges from '../timesheet/SubmittedWithoutChanges'

// Hooks/Utils
import { useHideInEnvironment } from '../../utils/hooks/useHideInEnvironment'

// Styles
const tabPageBoxSx = {
	height: '100%',
	paddingBlockStart: '.5rem',
	display: 'flex',
	flexDirection: 'column',
	alignSelf: 'stretch',
	gap: '1rem',
	overflowY: 'hidden',
}

const tabPageHeaderBoxSx = {
	display: 'flex',
	justifyContent: 'right',
	alignSelf: 'stretch',
}

/**
 * TabPageForApproval Component
 * @description This component represents the content of the "For Approval" tab in the timesheet management section. It includes a header with a button to toggle the visibility of filters, a table displaying drivers (motoristas) and their timesheets, and a section for timesheets that were submitted without changes. The component manages the state for showing or hiding the filters and conditionally renders the filter component based on that state.
 * @returns React.JSX.Element - The rendered TabPageForApproval component.
 * @see {@link TableMotoristaFilters} for the filters component used in this tab.
 * @see {@link TableMotoristas} for the table component displaying drivers and their timesheets.
 * @see {@link SubmittedWithoutChanges} for the section displaying timesheets submitted without changes.
 */
function TabPageForApproval(): React.JSX.Element {
	const isProd = useHideInEnvironment('prod')
	const [showFilters, setShowFilters] = useState(false)

	return (
		<Box sx={tabPageBoxSx}>
			<Box sx={tabPageHeaderBoxSx}>
				<CustomButton
					variant='text'
					size='small'
					onClick={() => setShowFilters(!showFilters)}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						transition: 'all 0.5s ease-in-out',
						'&:hover': {
							backgroundColor: 'transparent',
							color: 'primary.dark',
						},
					}}
				>
					<span>{showFilters ? 'Esconder Filtros' : 'Filtrar'}</span>
					<Box
						sx={{
							position: 'relative',
							overflow: 'hidden',
							width: 'fit-content',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '0.25rem',
						}}
					>
						{!showFilters ? (
							<FilterAltIcon
								fontSize='small'
								color='inherit'
							/>
						) : (
							<FilterAltOffIcon
								fontSize='small'
								color='inherit'
							/>
						)}
					</Box>
				</CustomButton>
			</Box>
			<TableMotoristaFilters showFilters={showFilters} />

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<TableMotoristas />
			</Box>
			{/**
			 * ! NOT TESTED, hide in production
			 */}
			{!isProd && <SubmittedWithoutChanges />}
		</Box>
	)
}

export default TabPageForApproval
